import styled from "@emotion/styled";
import { MoreVert } from "@mui/icons-material";
import { Avatar, Divider, IconButton, Stack, Typography } from "@mui/material";
import { Button } from "components/UI";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import { useMessagesStore } from "features/messages";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useApproveMember, useRejectMember } from "../api";
import { useMembersStore } from "../stores";

export const BasicProfileInformation = ({
  handleClick,
  handleExport,
  handlePrint
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const addRecipient = useMessagesStore(state => state.addRecipient);
  const clearRecipients = useMessagesStore(state => state.clearRecipients);
  const addMember = useMessagesStore(state => state.addMember);
  const removeMember = useMessagesStore(state => state.removeMember);
  const approveMutation = useApproveMember();
  const rejectMutation = useRejectMember();

  return (
    <BasicInfoSection>
      {fullMemberData?.status !== "pending" && (
        <IconButton
          aria-label="more"
          sx={{
            color: "white",
            alignSelf: "flex-end"
          }}
          onClick={handleClick}
        >
          <MoreVert />
        </IconButton>
      )}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing="20px"
        >
          <Avatar
            sx={{
              width: { xs: "120px", sm: "204px" },
              height: { xs: "120px", sm: "204px" },
              border: "5px solid",
              borderColor: "white"
            }}
            src={`${IMAGE_URL}/${fullMemberData?.profile_image_url}`}
            alt={fullMemberData?.first_name}
          />
          <Stack spacing={2}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {fullMemberData?.first_name} {fullMemberData?.father_name}{" "}
              {fullMemberData?.last_name}
            </Typography>
            <Stack>
              <Typography variant="subtitle2">
                {t("form.common.email")}
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {fullMemberData?.email}
              </Typography>
            </Stack>
            <Divider sx={{ background: "white" }} />
            <Stack
              direction="row"
              alignItems="center"
              spacing={{ xs: 3, sm: 5 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Stack>
                <Typography variant="subtitle2">
                  {t("form.common.phone")}
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {fullMemberData?.phone}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="subtitle2">
                  {t("form.common.gender")}
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {fullMemberData?.gender}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="subtitle2">
                  {t("form.common.age")}
                </Typography>
                {fullMemberData?.date_of_birth ? (
                  <Typography sx={{ fontWeight: 700 }}>
                    {moment().diff(
                      moment(fullMemberData?.date_of_birth),
                      "years"
                    )}
                  </Typography>
                ) : null}
              </Stack>
              <Stack>
                <Typography variant="subtitle2">
                  {t("addMembers.step3.MaritalStatus")}
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {fullMemberData?.marital_status}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        {fullMemberData?.status !== "pending" &&
          fullMemberData?.status !== "inactive" && (
            <Stack spacing={2}>
              {fullMemberData?.status !== "reject" && (
                <WhiteButton
                  variant="contained"
                  onClick={() => {
                    clearRecipients();
                    removeMember(fullMemberData.id);
                    addRecipient(
                      `${fullMemberData?.id}_${fullMemberData?.first_name}_${fullMemberData?.last_name}`,
                      fullMemberData
                    );
                    addMember(fullMemberData.id, {
                      id: fullMemberData.id
                    });
                    navigate("/messages");
                  }}
                >
                  {t("members.profile.SendMessage")}
                </WhiteButton>
              )}
              <WhiteButton
                variant="contained"
                onClick={() =>
                  navigate(`/members/${fullMemberData?.id}/update`)
                }
              >
                {t("members.profile.EditMember")}
              </WhiteButton>

              {fullMemberData?.status !== "reject" && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <LinkButton onClick={() => navigate(`/members/${id}/id`)}>
                    {t("members.profile.CreateID")}
                  </LinkButton>
                  <LinkButton onClick={handleExport}>
                    {t("members.profile.ExportProfile")}
                  </LinkButton>
                  <LinkButton onClick={handlePrint}>
                    {t("members.profile.PrintProfile")}
                  </LinkButton>
                </Stack>
              )}
            </Stack>
          )}

        {fullMemberData?.status === "pending" && (
          <Stack spacing={2}>
            <ApproveButton
              variant="contained"
              onClick={() =>
                approveMutation.mutate({
                  churchId: selectedChurch?.id,
                  memberId: fullMemberData?.id
                })
              }
              disabled={approveMutation.isLoading || rejectMutation.isLoading}
            >
              Approve
            </ApproveButton>
            <RejectButton
              variant="contained"
              onClick={() =>
                rejectMutation.mutate({
                  churchId: selectedChurch?.id,
                  memberId: fullMemberData?.id
                })
              }
              disabled={rejectMutation.isLoading || approveMutation.isLoading}
            >
              Reject
            </RejectButton>
            <WhiteButton
              variant="contained"
              onClick={() => navigate(`/members/${fullMemberData?.id}/update`)}
              disabled={rejectMutation.isLoading || approveMutation.isLoading}
            >
              {t("members.profile.EditMember")}
            </WhiteButton>
          </Stack>
        )}
      </Stack>
    </BasicInfoSection>
  );
};

const Section = styled.div`
  padding: 20px;
  border-radius: 8px;
  background: white;
  /* border: 0.5px solid gray; */
`;

const BasicInfoSection = styled(Section)`
  background: linear-gradient(90deg, #72d0d3 0%, #218596 103.53%);
  min-height: 272px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  color: white;
`;

const WhiteButton = styled(Button)`
  background: white;
  color: ${({ theme }) => theme.palette.primary.main};

  &:hover {
    background: white;
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const ApproveButton = styled(WhiteButton)`
  color: #22c55e;

  &:hover {
    color: #22c55e;
  }
`;

const RejectButton = styled(WhiteButton)`
  color: #ef4444;

  &:hover {
    color: #ef4444;
  }
`;

const LinkButton = styled.button`
  border: none;
  outline: none;
  background: none;
  color: white;
  border-bottom: 1px solid white;
  cursor: pointer;
`;
